import React from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Calendar } from 'lucide-react';
import { useEventTasks } from '@/hooks/useEventTasks';
import { useModalNavigation } from '@/hooks/useModalNavigation';
import { FamilyEvent } from '@/hooks/useOptimizedEvents';
import { SwipeableTaskItem } from '@/components/tasks/SwipeableTaskItem';
import { useFamily } from '@/hooks/useFamily';
import { useOptimizedTasks } from '@/hooks/useOptimizedTasks';

interface EventTaskSectionProps {
  event: FamilyEvent;
  familyId: string;
}

export function EventTaskSection({ event, familyId }: EventTaskSectionProps) {
  const { eventTasks, eventTaskLists, createEventTaskList, loading } = useEventTasks(familyId, event.id, event);
  const { openModal } = useModalNavigation();
  const { profile } = useFamily();
  const { completeTask } = useOptimizedTasks(familyId);

  // Debug logs
  console.log('EventTaskSection - eventTasks:', eventTasks);
  console.log('EventTaskSection - eventTaskLists:', eventTaskLists);
  console.log('EventTaskSection - eventId:', event.id);

  const handleCompleteTask = async (taskId: string): Promise<void> => {
    await completeTask(taskId);
  };

  const handleAddTask = async () => {
    // Chercher ou créer la liste liée à cet événement puis ouvrir le modal avec listId
    let targetListId = eventTaskLists[0]?.id;

    if (!targetListId) {
      const newList = await createEventTaskList();
      targetListId = newList?.id;
      console.log('EventTaskSection - created event task list id:', targetListId);
    } else {
      console.log('EventTaskSection - using existing event task list id:', targetListId);
    }

    openModal('createTask', { listId: targetListId, eventId: event.id });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-foreground">To-do event</h3>
        <div className="animate-pulse">
          <div className="h-20 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  // Total des tâches en cours pour cet événement
  const totalPendingTasks = eventTasks.filter(task => task.status === 'pending').length;

  return (
    <div className="space-y-4">
      {/* Title like in MobileTasks */}
      <h3 className="text-xl font-semibold text-foreground">
        To-do event ({totalPendingTasks})
      </h3>

      {/* Tasks Content */}
      <div>
        {eventTasks.filter(task => task.status === 'pending').length > 0 ? (
          <div className="space-y-3">
            {eventTasks
              .filter(task => task.status === 'pending')
              .map((task) => {
                 // Convert FamilyTask to TaskPreview format for SwipeableTaskItem
                 const taskPreview = {
                   id: task.id,
                   title: task.title,
                   due_date: task.due_date,
                   assigned_profile_ids: task.assigned_to_list || [task.assigned_to],
                   assignees: [] // This would need to be populated from the assigned_to array
                 };
                 
                  return (
                    <SwipeableTaskItem
                      key={task.id}
                      task={taskPreview}
                      onTaskClick={() => openModal('viewTask', { id: task.id })}
                      canComplete={task.status === 'pending'}
                      completeTask={handleCompleteTask}
                      isDraggable={false}
                      currentUserProfileId={profile?.id}
                      taskCreatedBy={task.created_by}
                    />
                  );
              })}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            Aucune tâche en cours
          </div>
        )}
        
        {/* Add Task Button - Same style as TaskListCard */}
        <div className="mt-4">
          <Button
            variant="ghost"
            onClick={handleAddTask}
            className="w-full border-2 border-dashed border-muted-foreground/30 rounded-lg p-3 bg-background hover:bg-muted/20 transition-colors group"
          >
            <div className="flex items-center justify-center gap-2 text-muted-foreground group-hover:text-foreground">
              <Plus className="h-4 w-4" />
              <span className="text-sm font-medium">Ajouter une tâche</span>
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
}